/** InsCoder 2026-09-14 B + Free gift bundle cart linking **/
(() => {
  if (customElements.get('free-gift-bundle')) return;

  const patchedProductForms = new WeakSet();
  const pendingQuickAddForms = new WeakSet();

  const getBundleInputs = (productForm) => {
    const formId = productForm.getAttribute('id');
    if (!formId) return [];

    return Array.from(document.querySelectorAll('input[name="bundles"]:checked, input[name="bundles"][data-free-gift-quick-add]')).filter((input) => {
      if (input.getAttribute('form') === formId || input.form?.id === formId) return true;

      let owner = input.parentElement;
      while (owner) {
        if (owner.id === formId || owner.getAttribute('form') === formId) return true;
        owner = owner.parentElement;
      }

      return false;
    });
  };

  const stableSerialize = (value) => {
    if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
    if (value && typeof value === 'object') {
      return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`;
    }
    return JSON.stringify(value);
  };

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index++) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return (result >>> 0).toString(36);
  };

  const getFreeGiftGroupId = (parentItem, giftInputs) => {
    const parentProperties = parentItem.properties || {};
    const groupSource = {
      parent: {
        id: String(parentItem.id),
        properties: parentProperties,
        selling_plan: parentItem.selling_plan || ''
      },
      gifts: giftInputs.map((input) => String(input.value)).sort()
    };

    return `free-gift-${hash(stableSerialize(groupSource))}`;
  };

  const addFreeGiftInputs = (form, giftInputs) => {
    const formId = form.getAttribute('id');
    const wrapper = document.createElement('div');
    wrapper.hidden = true;
    wrapper.setAttribute('form', formId);
    document.body.append(wrapper);

    const inputs = giftInputs.map((giftInput) => {
      const input = giftInput.cloneNode(true);
      input.removeAttribute('id');
      input.removeAttribute('form');
      input.removeAttribute('disabled');
      input.type = 'checkbox';
      input.setAttribute('data-free-gift-quick-add', '');
      input.checked = true;
      input.hidden = true;
      wrapper.append(input);
      return input;
    });

    return { inputs, wrapper };
  };

  const submitQuickAddWithFreeGifts = async (form, productUrl) => {
    const response = await fetch(productUrl, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Free gift configuration request failed with status ${response.status}`);

    const parsedHTML = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(parsedHTML.querySelectorAll('input[name="bundles"][data-free-gift]:checked'));
  };

  const patchProductForm = (productForm) => {
    if (!productForm || typeof productForm.prepareFormData !== 'function' || patchedProductForms.has(productForm)) return;

    const prepareFormData = productForm.prepareFormData.bind(productForm);

    productForm.prepareFormData = (formData) => {
      const preparedData = prepareFormData(formData);
      const bundleInputs = getBundleInputs(productForm).reverse();
      const parentItem = preparedData.items[preparedData.items.length - 1];
      if (!parentItem) return preparedData;

      if (bundleInputs.length > 0) {
        preparedData.items = bundleInputs.map((input) => ({
          id: input.value,
          quantity: 1
        }));
        preparedData.items.push(parentItem);
      }

      const giftInputs = bundleInputs.filter((input) => input.hasAttribute('data-free-gift'));
      if (giftInputs.length === 0) return preparedData;

      const parentQuantity = Number.parseInt(parentItem.quantity, 10);
      const giftQuantity = Number.isFinite(parentQuantity) && parentQuantity > 0 ? parentQuantity : 1;
      const groupId = getFreeGiftGroupId(parentItem, giftInputs);

      bundleInputs.forEach((input, index) => {
        if (!input.hasAttribute('data-free-gift') || !preparedData.items[index]) return;

        const giftProperties = {
          ...(preparedData.items[index].properties || {}),
          _free_gift_group: groupId,
          _free_gift_role: 'gift'
        };

        preparedData.items[index].quantity = giftQuantity;

        if (input.hasAttribute('data-free-gift-locked')) {
          giftProperties._free_gift_locked = 'true';
        }

        preparedData.items[index].properties = giftProperties;
      });

      parentItem.properties = {
        ...(parentItem.properties || {}),
        _free_gift_group: groupId,
        _free_gift_role: 'parent'
      };

      return preparedData;
    };

    patchedProductForms.add(productForm);
  };

  class FreeGiftBundle extends HTMLElement {
    connectedCallback() {
      const productForm = document.getElementById(this.getAttribute('form'));
      if (customElements.get('product-form')) {
        patchProductForm(productForm);
        return;
      }

      customElements.whenDefined('product-form').then(() => patchProductForm(productForm));
    }
  }

  if (!customElements.get('free-gift-bundle')) {
    customElements.define('free-gift-bundle', FreeGiftBundle);
  }

  const updateLinkedGiftItems = async (target, quantity) => {
    const cartItems = target.closest('cart-items');
    const parentKey = target.getAttribute('data-index');
    const giftKeys = (target.getAttribute('data-free-gift-keys') || '').split(',').filter(Boolean);

    if (!cartItems || !parentKey || giftKeys.length === 0 || target.getAttribute('aria-busy') === 'true') return false;

    target.setAttribute('aria-busy', 'true');
    cartItems.enableLoading(parentKey);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const updates = { [parentKey]: quantity };
    giftKeys.forEach((key) => {
      updates[key] = quantity;
    });

    try {
      const response = await fetch(theme.routes.cart_update_url, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify({ updates, sections: sectionsToBundle })
      });
      if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

      const parsedState = await response.json();

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift-bundle',
        cart: parsedState,
        target,
        line: parentKey
      });
      return true;
    }
    catch (error) {
      console.error(error);
      cartItems.disableLoading(parentKey);
      target.removeAttribute('aria-busy');
      return false;
    }
  };

  const passThroughSubmit = (form) => {
    form.setAttribute('data-free-gift-pass-through', 'true');
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    form.removeAttribute('data-free-gift-pass-through');
  };

  const handleQuickAddSubmit = async (form) => {
    if (pendingQuickAddForms.has(form)) return;

    pendingQuickAddForms.add(form);

    const submitButton = form.querySelector('[type="submit"]');
    submitButton?.setAttribute('aria-busy', 'true');

    try {
      const productUrl = form.getAttribute('data-free-gift-product-url');
      const giftInputs = await submitQuickAddWithFreeGifts(form, productUrl);

      if (giftInputs.length === 0) {
        passThroughSubmit(form);
        return;
      }

      const temporaryGiftInputs = addFreeGiftInputs(form, giftInputs);
      patchProductForm(form);
      passThroughSubmit(form);
      temporaryGiftInputs.wrapper.remove();
    }
    catch (error) {
      console.error(error);
      passThroughSubmit(form);
    }
    finally {
      pendingQuickAddForms.delete(form);
      submitButton?.removeAttribute('aria-busy');
    }
  };

  const isQuickAddForm = (form) => form instanceof HTMLFormElement
    && form.getAttribute('is') === 'product-form'
    && form.getAttribute('data-free-gift-product-url');

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!isQuickAddForm(form) || form.hasAttribute('data-free-gift-pass-through') || pendingQuickAddForms.has(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    handleQuickAddSubmit(form);
  }, true);

  document.addEventListener('click', (event) => {
    const submitter = event.target.closest?.('[type="submit"]');
    const form = submitter?.form;
    if (!isQuickAddForm(form) || pendingQuickAddForms.has(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    handleQuickAddSubmit(form);
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('[data-free-gift-remove]');
    if (!target) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    updateLinkedGiftItems(target, 0);
  }, true);

  document.addEventListener('change', (event) => {
    const target = event.target.closest?.('[data-free-gift-quantity]');
    if (!target) return;

    const quantity = Number.parseInt(target.value, 10);
    if (!Number.isFinite(quantity) || quantity < 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    updateLinkedGiftItems(target, quantity);
  }, true);
})();
/** InsCoder 2026-09-14 E + Free gift bundle cart linking **/
